import { Component } from '@theme/component';
import { setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * Custom header mega menu behavior for the metaobject-shaped navigation source.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem
 *
 * @extends {Component}
 */
class HeaderMegaMenu extends Component {
  /** @type {number | undefined} */
  #openTimer;

  /** @type {number | undefined} */
  #closeTimer;

  /** @type {MutationObserver | null} */
  #submenuMutationObserver = null;

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {State} */
  #state = {
    activeItem: null,
  };

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.addEventListener('pointerenter', this.#handlePointerEnter, { signal, capture: true });
    this.addEventListener('pointerleave', this.#handlePointerLeave, { signal });
    this.addEventListener('focusin', this.#handleFocusIn, { signal });
    this.addEventListener('focusout', this.#handleFocusOut, { signal });
    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    window.addEventListener('resize', this.#handleResize, { signal });

  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController?.abort();
    this.#abortController = null;
    this.#clearTimers();
    this.#cleanupMutationObserver();
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  get openDelay() {
    return Number(this.dataset.openDelay || 150);
  }

  get closeDelay() {
    return Number(this.dataset.closeDelay || 350);
  }

  /** @param {Event} event */
  #handlePointerEnter = (event) => {
    if (!(event.target instanceof Element)) return;

    const item = event.target.closest('[data-header-mega-menu-item]');
    if (!(item instanceof HTMLElement) || !this.contains(item)) return;

    this.#scheduleActivate(item, this.openDelay);
  };

  #handlePointerLeave = () => {
    this.#scheduleDeactivate(this.closeDelay);
  };

  /** @param {FocusEvent} event */
  #handleFocusIn = (event) => {
    if (!(event.target instanceof Element)) return;

    const item = event.target.closest('[data-header-mega-menu-item]');
    if (!(item instanceof HTMLElement) || !this.contains(item)) return;

    this.#scheduleActivate(item, 0);
  };

  /** @param {FocusEvent} event */
  #handleFocusOut = (event) => {
    const nextTarget = event.relatedTarget;
    if (nextTarget instanceof Node && this.contains(nextTarget)) return;

    this.#scheduleDeactivate(this.closeDelay);
  };

  /** @param {KeyboardEvent} event */
  #handleKeyDown = (event) => {
    if (event.key !== 'Escape') return;

    const activeLink = this.#state.activeItem?.querySelector('[aria-expanded]');

    this.#deactivate();

    if (activeLink instanceof HTMLElement) activeLink.focus();
  };

  #handleResize = () => {
    setHeaderMenuStyle();

    if (this.#state.activeItem) {
      this.#setOpenHeights(this.#state.activeItem);
    }
  };

  /**
   * @param {HTMLElement} item
   * @param {number} delay
   */
  #scheduleActivate(item, delay) {
    window.clearTimeout(this.#closeTimer);

    if (item === this.#state.activeItem) return;

    window.clearTimeout(this.#openTimer);
    this.#openTimer = window.setTimeout(() => {
      this.#activate(item);
    }, delay);
  }

  /** @param {number} delay */
  #scheduleDeactivate(delay) {
    window.clearTimeout(this.#openTimer);
    window.clearTimeout(this.#closeTimer);

    this.#closeTimer = window.setTimeout(() => {
      if (this.matches(':hover')) return;
      this.#deactivate();
    }, delay);
  }

  /** @param {HTMLElement} item */
  #activate(item) {
    this.dispatchEvent(new MegaMenuHoverEvent());

    const trigger = getItemTrigger(item);
    const submenu = getItemSubmenu(item);

    if (!trigger || !submenu || !this.headerComponent) {
      this.#deactivate();
      return;
    }

    if (this.#state.activeItem && this.#state.activeItem !== item) {
      this.#setItemExpanded(this.#state.activeItem, false);
      delete getItemSubmenu(this.#state.activeItem)?.dataset.active;
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    this.#setItemExpanded(item, true);
    submenu.dataset.active = '';
    this.#preloadImages(item);

    this.#watchSubmenuHeight(submenu);
    this.#setOpenHeights(item);
  }

  #deactivate() {
    window.clearTimeout(this.#openTimer);
    window.clearTimeout(this.#closeTimer);

    const item = this.#state.activeItem;
    if (!item) return;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.#setItemExpanded(item, false);

    const submenu = getItemSubmenu(item);
    if (submenu) delete submenu.dataset.active;

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    this.#cleanupMutationObserver();
  }

  /** @param {HTMLElement} item */
  #setOpenHeights(item) {
    const submenu = getItemSubmenu(item);
    const submenuHeight = submenu?.offsetHeight || 0;

    this.headerComponent?.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.#setFullOpenHeaderHeight(submenuHeight);
    this.style.setProperty('--submenu-opacity', '1');
  }

  /**
   * @param {HTMLElement} item
   * @param {boolean} expanded
   */
  #setItemExpanded(item, expanded) {
    const trigger = getItemTrigger(item);
    if (trigger) trigger.ariaExpanded = expanded.toString();
  }

  /** @param {HTMLElement} submenu */
  #watchSubmenuHeight(submenu) {
    this.#cleanupMutationObserver();

    this.#submenuMutationObserver = new MutationObserver(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (!this.#state.activeItem || submenu.offsetHeight <= 0) return;

          this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
          this.#setFullOpenHeaderHeight(submenu.offsetHeight);
          this.#cleanupMutationObserver();
        });
      });
    });

    this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });

    window.setTimeout(() => {
      this.#cleanupMutationObserver();
    }, 500);
  }

  /** @param {number} submenuHeight */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');
    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;
    const fullOpenHeaderHeight = submenuHeight === 0 ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  #preloadImages = (item) => {
    const images = item.querySelectorAll('img[loading="lazy"]');
    for (const image of images) {
      image.removeAttribute('loading');
    }
  };

  #clearTimers() {
    window.clearTimeout(this.#openTimer);
    window.clearTimeout(this.#closeTimer);
  }

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-mega-menu')) {
  customElements.define('header-mega-menu', HeaderMegaMenu);
}

/**
 * @param {HTMLElement} item
 * @returns {HTMLElement | null}
 */
function getItemTrigger(item) {
  return /** @type {HTMLElement | null} */ (item.querySelector('[aria-expanded]'));
}

/**
 * @param {HTMLElement} item
 * @returns {HTMLElement | null}
 */
function getItemSubmenu(item) {
  return /** @type {HTMLElement | null} */ (item.querySelector('.header-mega-menu__submenu'));
}
